import React, { useEffect, useState } from "react";
import {
  CalendarHeart,
  Trophy,
  Flame,
  LayoutGrid,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";
import AdminSectionCard from "./AdminSectionCard";
import widgetsService from "../../services/widgetsService";
import ImportantDatesSection from "./ImportantDatesSection";
import { useTranslation, useFormat } from "../../i18n";

// Icons per widget key. Labels and descriptions are looked up in the component.
const WIDGET_ICONS = {
  important_dates: CalendarHeart,
  leaderboard: Trophy,
  streaks: Flame,
};

/** Matches the switch used across the other admin tabs. */
const PillToggle = ({ checked, onChange, label, disabled = false }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    disabled={disabled}
    aria-pressed={checked}
    aria-label={label}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
      checked ? "bg-purple-600" : "bg-slate-300"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

/**
 * Collapsible group of related settings.
 *
 * Open by default: the grouping is there to stop the settings running together
 * as one flat wall, not to hide them. Opening every group by hand to find one
 * field cost more than the grouping saved.
 */
function SubSection({ title, summary, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-slate-50/60 hover:bg-slate-100/70 transition-colors text-left"
      >
        <span className="text-sm font-semibold text-slate-800 flex-1">
          {title}
        </span>
        {summary && !open && (
          <span className="text-xs text-slate-500 flex-shrink-0">{summary}</span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${
            open ? "" : "-rotate-90"
          }`}
        />
      </button>
      {open && (
        <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-slate-200">
          {children}
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}

const controlBase =
  "text-sm border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent";
const controlClass = `w-full px-3 sm:px-4 py-1.5 sm:py-2 ${controlBase}`;
const numberClass = `w-20 px-3 py-1.5 sm:py-2 text-center ${controlBase}`;
// Padding is set per side rather than with `px-*`: a `pr-10` alongside
// `sm:px-4` loses to the responsive variant above the sm breakpoint, which put
// the option text back under the chevron on exactly the widths that matter.
const selectClass = `w-full pl-3 sm:pl-4 pr-10 py-1.5 sm:py-2 appearance-none cursor-pointer ${controlBase}`;

/**
 * A select with room around its arrow.
 *
 * The native arrow is painted hard against the right edge of the control,
 * where `padding-right` cannot move it — on a pill-shaped input that puts it
 * inside the curve, touching the border. So the native one is switched off and
 * a chevron is drawn where there is space for it, which also makes the arrow
 * match the ones the rest of this tab uses.
 */
function Select({ value, onChange, children, ...rest }) {
  return (
    <span className="relative block">
      <select
        {...rest}
        value={value}
        onChange={onChange}
        className={selectClass}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
      />
    </span>
  );
}

function LeaderboardConfig({ config, onChange }) {
  const { t } = useTranslation("admin");
  const metric = config.metric || "count";
  const scope = config.scope || "org";
  const boardLabels = {
    received: t("widgets.leaderboard.board_received"),
    given: t("widgets.leaderboard.board_given"),
    streaks: t("widgets.leaderboard.board_streaks"),
  };
  const periodLabels = {
    day: t("widgets.leaderboard.period_day"),
    week: t("widgets.leaderboard.period_week"),
    month: t("widgets.leaderboard.period_month"),
    year: t("widgets.leaderboard.period_year"),
  };
  const board = config.default_board || "received";
  const period = config.default_period || "week";
  return (
    <div className="space-y-3 sm:space-y-4">
      <SubSection
        title={t("widgets.leaderboard.ranking")}
        summary={`${
          metric === "points" ? t("widgets.leaderboard.metric_points") : t("widgets.leaderboard.metric_count")
        } · ${
          scope === "team" ? t("widgets.leaderboard.scope_team_short") : t("widgets.leaderboard.scope_org")
        }`}
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("widgets.leaderboard.rank_by")} hint={t("widgets.leaderboard.rank_by_hint")}>
            <Select
              value={metric}
              onChange={(e) => onChange({ metric: e.target.value })}
            >
              <option value="count">{t("widgets.leaderboard.metric_count")}</option>
              <option value="points">{t("widgets.leaderboard.metric_points")}</option>
            </Select>
          </Field>
          <Field
            label={t("widgets.leaderboard.default_scope")}
            hint={t("widgets.leaderboard.default_scope_hint")}
          >
            <Select
              value={scope}
              onChange={(e) => onChange({ scope: e.target.value })}
            >
              <option value="org">{t("widgets.leaderboard.scope_org")}</option>
              <option value="team">{t("widgets.leaderboard.scope_team")}</option>
            </Select>
          </Field>
        </div>
      </SubSection>

      <SubSection
        title={t("widgets.leaderboard.defaults")}
        summary={`${boardLabels[board] || board} · ${periodLabels[period] || period}`}
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("widgets.leaderboard.board")}>
            <Select
              value={board}
              onChange={(e) => onChange({ default_board: e.target.value })}
            >
              <option value="received">{t("widgets.leaderboard.board_received")}</option>
              <option value="given">{t("widgets.leaderboard.board_given")}</option>
              <option value="streaks">{t("widgets.leaderboard.board_streaks")}</option>
            </Select>
          </Field>
          <Field label={t("widgets.leaderboard.period")}>
            <Select
              value={period}
              onChange={(e) => onChange({ default_period: e.target.value })}
            >
              <option value="day">{t("widgets.leaderboard.period_day")}</option>
              <option value="week">{t("widgets.leaderboard.period_week")}</option>
              <option value="month">{t("widgets.leaderboard.period_month")}</option>
              <option value="year">{t("widgets.leaderboard.period_year")}</option>
            </Select>
          </Field>
        </div>
        <p className="text-xs text-slate-500 mt-2">{t("widgets.leaderboard.members_can_change")}</p>
      </SubSection>
    </div>
  );
}

// 0 = Monday, matching Python's weekday() — the backend stores it that way and
// a second convention on the client is how off-by-one boundaries get shipped.
const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const weekdayLabels = (t) => [
  t("widgets.weekdays.monday"),
  t("widgets.weekdays.tuesday"),
  t("widgets.weekdays.wednesday"),
  t("widgets.weekdays.thursday"),
  t("widgets.weekdays.friday"),
  t("widgets.weekdays.saturday"),
  t("widgets.weekdays.sunday"),
];

// Hour-of-day labels in the admin's locale (e.g. "12:00 AM" / "00:00").
const hourLabel = (fmt, hour) =>
  fmt.date(new Date(2000, 0, 1, hour, 0, 0), { hour: "numeric", minute: "2-digit" });

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

// A curated list rather than the full IANA set: an admin picking their own
// office's timezone does not need 600 options, and the current value is always
// appended so a zone set elsewhere is never silently dropped.
const COMMON_ZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

export function describeSchedule(schedule, t, fmt) {
  if (!schedule) return "";
  const days = weekdayLabels(t);
  const day = days[schedule.week_start_day ?? 0] || days[0];
  const hour = hourLabel(fmt, schedule.week_start_hour ?? 0);
  return t("widgets.schedule.summary", { day, hour });
}

/**
 * When the streak week turns over.
 *
 * Org-level rather than part of the widget's config blob, because the
 * leaderboard's "this week" has to be the same week the streak counts. It is
 * edited here because this is where an admin looks for it: a streak is the one
 * feature whose whole point is a deadline.
 */
function ScheduleConfig({ schedule, onChange }) {
  const { t } = useTranslation("admin");
  const fmt = useFormat();
  if (!schedule) return null;
  const zones = COMMON_ZONES.includes(schedule.timezone)
    ? COMMON_ZONES
    : [schedule.timezone, ...COMMON_ZONES];
  const days = weekdayLabels(t);

  return (
    <SubSection
      title={t("widgets.schedule.title")}
      summary={describeSchedule(schedule, t, fmt)}
      defaultOpen={false}
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label={t("widgets.schedule.week_starts")}>
          <Select
            value={schedule.week_start_day ?? 0}
            onChange={(e) => onChange({ week_start_day: Number(e.target.value) })}
          >
            {WEEKDAY_KEYS.map((key, index) => (
              <option key={key} value={index}>
                {days[index]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("widgets.schedule.at")}>
          <Select
            value={schedule.week_start_hour ?? 0}
            onChange={(e) =>
              onChange({ week_start_hour: Number(e.target.value) })
            }
          >
            {HOURS.map((hour) => (
              <option key={hour} value={hour}>
                {hourLabel(fmt, hour)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("widgets.schedule.timezone")}>
          <Select
            value={schedule.timezone || "UTC"}
            onChange={(e) => onChange({ timezone: e.target.value })}
          >
            {zones.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <p className="text-xs text-slate-500 mt-2">
        {t("widgets.schedule.hint")}
      </p>
    </SubSection>
  );
}

function StreaksConfig({ config, onChange, schedule, onScheduleChange, headcount }) {
  const { t } = useTranslation("admin");
  const fmt = useFormat();
  const weights = config.weights || {};
  const caps = config.caps || {};
  const threshold = Number(config.threshold || 0);
  const reactionCeiling = Number(caps.reaction || 0);
  const reactionsAloneEnough = reactionCeiling >= threshold && threshold > 0;
  const label = config.currency_label || t("widgets.streaks.default_currency");

  const amount = Number(config.points_reward_amount || 0);
  const projectedAnnual = amount * 52 * (headcount || 0);

  const kindLabels = {
    reaction: t("widgets.streaks.kind_reaction"),
    comment: t("widgets.streaks.kind_comment"),
    recognition: t("widgets.streaks.kind_recognition"),
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <SubSection title={t("widgets.streaks.earning")} summary={t("widgets.streaks.earning_summary", { threshold, label })}>
        <div className="space-y-3">
          {["reaction", "comment", "recognition"].map((kind) => (
            <div key={kind} className="flex items-center gap-2 flex-wrap">
              <span className="w-24 text-sm font-medium text-slate-700 capitalize">
                {kindLabels[kind] || kind}
              </span>
              <input
                type="number"
                min={0}
                aria-label={t("widgets.streaks.kind_value", { kind: kindLabels[kind] || kind })}
                className={numberClass}
                value={weights[kind] ?? 0}
                onChange={(e) =>
                  onChange({ weights: { [kind]: Number(e.target.value) } })
                }
              />
              <span className="text-xs text-slate-500">{t("widgets.streaks.each_max")}</span>
              <input
                type="number"
                min={0}
                aria-label={t("widgets.streaks.kind_cap", { kind: kindLabels[kind] || kind })}
                className={numberClass}
                value={caps[kind] ?? 0}
                onChange={(e) =>
                  onChange({ caps: { [kind]: Number(e.target.value) } })
                }
              />
              <span className="text-xs text-slate-500">{t("widgets.streaks.per_week")}</span>
            </div>
          ))}

          <Field label={t("widgets.streaks.weekly_target")}>
            <input
              type="number"
              min={1}
              className={numberClass}
              value={config.threshold ?? 7}
              onChange={(e) => onChange({ threshold: Number(e.target.value) })}
            />
          </Field>

          {/* The caps decide whether the streak means anything, so the outcome
              of the current numbers is stated rather than left to be worked out. */}
          <div
            className={`rounded-xl border px-3 py-2 text-xs flex items-start gap-1.5 ${
              reactionsAloneEnough
                ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-slate-50 text-slate-600 border-slate-200"
            }`}
          >
            {reactionsAloneEnough && (
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            )}
            <span>
              {t("widgets.streaks.reactions_reach", { ceiling: reactionCeiling, threshold })}
              {" "}
              {reactionsAloneEnough
                ? t("widgets.streaks.reactions_enough")
                : t("widgets.streaks.comment_required")}
            </span>
          </div>
        </div>
      </SubSection>

      <ScheduleConfig schedule={schedule} onChange={onScheduleChange} />

      <SubSection
        title={t("widgets.streaks.missed_weeks")}
        summary={config.reset_mode === "hard_reset" ? t("widgets.streaks.mode_reset") : t("widgets.streaks.mode_freeze")}
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("widgets.streaks.on_missed_week")}>
            <Select
              value={config.reset_mode || "freeze"}
              onChange={(e) => onChange({ reset_mode: e.target.value })}
            >
              <option value="freeze">{t("widgets.streaks.use_freeze")}</option>
              <option value="hard_reset">{t("widgets.streaks.reset_to_zero")}</option>
            </Select>
          </Field>
          {config.reset_mode !== "hard_reset" && (
            <Field
              label={t("widgets.streaks.freezes_allowed")}
              hint={t("widgets.streaks.freezes_per_weeks", { count: config.freeze_window_weeks || 8 })}
            >
              <input
                type="number"
                min={0}
                className={numberClass}
                value={config.freeze_allowance ?? 1}
                onChange={(e) =>
                  onChange({ freeze_allowance: Number(e.target.value) })
                }
              />
            </Field>
          )}
        </div>
      </SubSection>

      <SubSection title={t("widgets.streaks.naming")} summary={label}>
        <Field label={t("widgets.streaks.currency_name")} hint={t("widgets.streaks.currency_hint")}>
          <input
            type="text"
            className={controlClass}
            value={config.currency_label || ""}
            placeholder={t("widgets.streaks.default_currency")}
            onChange={(e) => onChange({ currency_label: e.target.value })}
          />
        </Field>
      </SubSection>

      <SubSection
        title={t("widgets.streaks.points_reward")}
        summary={config.points_reward_enabled ? t("widgets.streaks.reward_summary", { amount }) : t("widgets.streaks.off")}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-slate-700">
            {t("widgets.streaks.award_points_label")}
          </span>
          <PillToggle
            label={t("widgets.streaks.award_points")}
            checked={!!config.points_reward_enabled}
            onChange={(v) => onChange({ points_reward_enabled: v })}
          />
        </div>

        {config.points_reward_enabled && (
          <div className="mt-4 space-y-4">
            <Field label={t("widgets.streaks.points_per_week")}>
              <input
                type="number"
                min={0}
                className={numberClass}
                value={config.points_reward_amount ?? 0}
                onChange={(e) =>
                  onChange({ points_reward_amount: Number(e.target.value) })
                }
              />
            </Field>

            <Field
              label={t("widgets.streaks.budget_id_label")}
              hint={t("widgets.streaks.budget_id_hint")}
            >
              <input
                type="text"
                className={controlClass}
                value={config.engagement_budget_id || ""}
                placeholder={t("widgets.streaks.budget_id_placeholder")}
                onChange={(e) =>
                  onChange({ engagement_budget_id: e.target.value || null })
                }
              />
            </Field>

            {/* Points are dollars and this scales with headcount, so the cost is
                shown while the number is being chosen. */}
            {amount > 0 && headcount > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-xs">
                {t("widgets.streaks.projection", {
                  amount: fmt.currency(projectedAnnual, "USD", { maximumFractionDigits: 0 }),
                  count: headcount,
                })}
              </div>
            )}
          </div>
        )}
      </SubSection>
    </div>
  );
}

export default function WidgetsSection({ headcount = 0 }) {
  const { t } = useTranslation("admin");
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  // Everything starts expanded; this tracks only what the admin has folded
  // away, so the tab opens showing its settings rather than three closed rows.
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());

  const widgetMeta = {
    important_dates: {
      label: t("widgets.meta.important_dates.label"),
      description: t("widgets.meta.important_dates.description"),
    },
    leaderboard: {
      label: t("widgets.meta.leaderboard.label"),
      description: t("widgets.meta.leaderboard.description"),
    },
    streaks: {
      label: t("widgets.meta.streaks.label"),
      description: t("widgets.meta.streaks.description"),
    },
  };

  const load = () => {
    widgetsService
      .adminListWidgets()
      .then((data) => setWidgets(Array.isArray(data) ? data : []))
      .catch(() => setError(t("widgets.errors.load")))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleCollapsed = (key) =>
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const patch = async (key, payload) => {
    setSaving(key);
    setError(null);
    try {
      const updated = await widgetsService.adminUpdateWidget(key, payload);
      setWidgets((prev) => prev.map((w) => (w.key === key ? updated : w)));
    } catch {
      setError(t("widgets.errors.save"));
    } finally {
      setSaving(null);
    }
  };

  const move = async (key, direction) => {
    const order = [...widgets]
      .sort((a, b) => a.position - b.position)
      .map((w) => w.key);
    const index = order.indexOf(key);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target], order[index]];
    setSaving(key);
    try {
      setWidgets(await widgetsService.adminReorderWidgets(order));
    } catch {
      setError(t("widgets.errors.reorder"));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="py-16 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const sorted = [...widgets].sort((a, b) => a.position - b.position);

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg sm:text-2xl font-bold text-slate-800">{t("widgets.title")}</h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">
          {t("widgets.subtitle")}
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {sorted.map((widget, index) => {
        const meta = {
          label: widgetMeta[widget.key]?.label || widget.key,
          icon: WIDGET_ICONS[widget.key] || LayoutGrid,
          description: widgetMeta[widget.key]?.description || null,
        };
        const open = !collapsedKeys.has(widget.key);

        return (
          <AdminSectionCard
            key={widget.key}
            icon={meta.icon}
            title={meta.label}
            description={meta.description}
            action={
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex flex-col -my-1">
                  <button
                    type="button"
                    aria-label={t("widgets.move_up", { name: meta.label })}
                    disabled={index === 0 || saving === widget.key}
                    onClick={() => move(widget.key, -1)}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={t("widgets.move_down", { name: meta.label })}
                    disabled={
                      index === sorted.length - 1 || saving === widget.key
                    }
                    onClick={() => move(widget.key, 1)}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                <span
                  className={`hidden sm:inline text-xs sm:text-sm font-medium ${
                    widget.enabled ? "text-purple-700" : "text-slate-500"
                  }`}
                >
                  {widget.enabled ? t("widgets.enabled") : t("widgets.disabled")}
                </span>
                <PillToggle
                  label={t("widgets.enable_widget", { name: meta.label })}
                  checked={widget.enabled}
                  onChange={(v) => patch(widget.key, { enabled: v })}
                />

                <button
                  type="button"
                  onClick={() => toggleCollapsed(widget.key)}
                  aria-label={open ? t("widgets.hide_settings") : t("widgets.show_settings")}
                  aria-expanded={open}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      open ? "" : "-rotate-90"
                    }`}
                  />
                </button>
              </div>
            }
          >
            {open ? (
              <div className="space-y-3 sm:space-y-4">
                {!widget.enabled && (
                  <p className="text-xs text-slate-500">
                    {t("widgets.widget_off")}
                  </p>
                )}
                {widget.key === "leaderboard" && (
                  <LeaderboardConfig
                    config={widget.config || {}}
                    onChange={(config) => patch(widget.key, { config })}
                  />
                )}
                {widget.key === "streaks" && (
                  <StreaksConfig
                    config={widget.config || {}}
                    schedule={widget.schedule}
                    headcount={headcount}
                    onChange={(config) => patch(widget.key, { config })}
                    onScheduleChange={(fields) => patch(widget.key, fields)}
                  />
                )}
                {widget.key === "important_dates" && (
                  <ImportantDatesSection embedded />
                )}
              </div>
            ) : null}
          </AdminSectionCard>
        );
      })}
    </div>
  );
}
